import React from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSearchParams } from 'react-router-dom'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { useAuthStore } from 'Store/Auth'

const initialValues = {
  password: '',
  confirmPassword: '',
}

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(3, 'ต้องมีอย่างน้อย 3 ตัวอักษร')
    .max(50, 'สูงสุด 50 ตัวอักษร')
    .required('กรุณากรอกรหัสผ่าน'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'รหัสผ่านไม่ตรงกัน')
    .required('กรุณายืนยันรหัสผ่าน'),
})

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const resetToken = searchParams.get('token')
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const resetPassword = useAuthStore(state => state.resetPassword)
  const formik = useFormik({
    initialValues,
    validationSchema: resetPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        if (resetToken)
          resetPassword(values.password, resetToken).then(({ success, data }) => {
            if (success) {
              setHasErrors(false)
              setLoading(false)
              setSubmitting(false)
            } else {
              setHasErrors(true)
              setLoading(false)
              setSubmitting(false)
              setStatus(data)
            }
          })
      }, 1000)
    },
  })

  return (
    <div className='position-relative h-100 login-bg'>
      <div
        className='card rounded-3 w-xl-550px w-100 mx-auto position-absolute top-50'
        style={{ left: '50%', transform: 'translate(-50%, -50%)' }}>
        <div className='card-body p-10 p-xl-20'>
          <form
            className='form fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_login_password_reset_form'
            onSubmit={formik.handleSubmit}>
            <div className='text-center mb-10'>
              {/* begin::Title */}
              <h1 className='text-dark fw-bolder mb-3'>เปลี่ยนรหัสผ่าน</h1>
              {/* end::Title */}

              {/* begin::Link */}
              <div className='text-gray-500 fw-semibold fs-6'></div>
              {/* end::Link */}
            </div>

            {/* begin::Title */}
            {hasErrors === true && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>
                  เปลี่ยนรหัสผ่านไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
                </div>
              </div>
            )}

            {hasErrors === false && (
              <div className='mb-10 bg-light-info p-8 rounded'>
                <div className='text-info'>เปลี่ยนรหัสผ่านสำเร็จ คุณสามารถเข้าสู่ระบบได้</div>
              </div>
            )}
            {/* end::Title */}

            {/* begin::Form group */}
            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>รหัสผ่าน</label>
              <input
                type='text'
                placeholder=''
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control bg-transparent',
                  { 'is-invalid': formik.touched.password && formik.errors.password },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='fv-row mb-8'>
              <label className='form-label fw-bolder text-gray-900 fs-6'>ยืนยันรหัสผ่าน</label>
              <input
                type='text'
                placeholder=''
                autoComplete='off'
                {...formik.getFieldProps('confirmPassword')}
                className={clsx(
                  'form-control bg-transparent',
                  { 'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword },
                  {
                    'is-valid': formik.touched.confirmPassword && !formik.errors.confirmPassword,
                  }
                )}
              />
              {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.confirmPassword}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
              {(hasErrors === undefined || hasErrors === true) && (
                <button
                  type='submit'
                  id='kt_password_reset_submit'
                  className='btn btn-primary me-4'
                  disabled={formik.isSubmitting}>
                  <span className='indicator-label'>ยืนยัน</span>
                  {loading && (
                    <span className='indicator-progress'>
                      กรุณารอ
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              )}
              {hasErrors === false && (
                <Link to='/auth/login'>
                  <button
                    type='submit'
                    id='kt_login_password_reset_form_cancel_button'
                    className='btn btn-light'
                    disabled={formik.isSubmitting}>
                    ยกเลิก
                  </button>
                </Link>
              )}
            </div>
            {/* end::Form group */}
          </form>
        </div>
      </div>
    </div>
  )
}
